import { useId, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { NumberInput } from '../ui/NumberInput';
import { Spinner } from '../../ui/Spinner';
import { btnDestructive, btnGhost, btnPrimary, inputCls } from '../ui/styles';
import { useSubmit } from '../../hooks/useSubmit';
import { addStockMovement } from '../adminApi';
import { useRequestId } from '../../hooks/useRequestId';
import { toCents } from '../../core/format/currency';
import { locationLabel, movementProblem } from '../../core/rules/stockMovements';
import { stockLocations, type StockItem, type StockLocation, type StockMovementInput, type StockMovementRequest, type StockMovementType, type WriteOffCause } from '../../core/types/stock';
type Props = {
  item: StockItem;
  type: StockMovementType;
  onDone: (item: StockItem) => void;
  onCancel: () => void;
};
const causes: {
  key: WriteOffCause;
  label: string;
}[] = [{
  key: 'kapot',
  label: 'Kapot'
}, {
  key: 'over-datum',
  label: 'Over datum'
}, {
  key: 'tester',
  label: 'Tester'
}, {
  key: 'cadeau',
  label: 'Cadeau'
}, {
  key: 'marketing',
  label: 'Marketing of sample'
}, {
  key: 'anders',
  label: 'Anders'
}];
const intro: Record<StockMovementType, string> = {
  overboeken: 'Van de ene plek naar de andere. Het totaal blijft gelijk.',
  verbruik: 'Een verpakking opengemaakt voor gebruik in de salon of bij een behandeling.',
  inkoop: 'Een levering binnen: komt erbij op de plek waar je hem neerzet.',
  afschrijven: 'Weg zonder verkoop: kapot, over datum, tester, cadeau of weggegeven voor marketing.',
  telling: 'Vul in wat je telt. De verandering is het verschil met wat er stond.'
};
const success: Record<StockMovementType, string> = {
  overboeken: 'Overgeboekt',
  verbruik: 'Geboekt als in gebruik',
  inkoop: 'Inkoop geboekt',
  afschrijven: 'Afgeschreven',
  telling: 'Telling opgeslagen'
};

/** Eén voorraadactie op één artikel. Pas na het antwoord van de server is het geboekt. */
export function StockActionForm({
  item,
  type,
  onDone,
  onCancel
}: Props) {
  const id = useId();
  const preferred: StockLocation = item.kind === 'verbruik' || type === 'verbruik' ? 'salon' : 'webshop';
  // Overboeken begint op een plek die bijgehouden wordt (de andere kan niet als bron).
  const defaultLocation: StockLocation = type === 'overboeken' && item.levels[preferred] === null ? stockLocations.find(l => item.levels[l] !== null) ?? preferred : preferred;
  const [location, setLocation] = useState<StockLocation>(defaultLocation);
  const [to, setTo] = useState<StockLocation>('salon');
  const [qty, setQty] = useState<number | undefined>(type === 'verbruik' ? 1 : undefined);
  const [counted, setCounted] = useState<number | undefined>(undefined);
  const [cost, setCost] = useState<number | undefined>(item.costPriceCents === undefined ? undefined : item.costPriceCents / 100);
  const [cause, setCause] = useState<WriteOffCause>('kapot');
  const [note, setNote] = useState('');
  const [touched, setTouched] = useState(false);
  const {
    submit,
    submitting,
    error
  } = useSubmit(addStockMovement);
  const from = location;
  const target = to === from ? stockLocations.find(l => l !== from) ?? to : to;
  const body = ((): StockMovementInput => {
    const base = {
      itemId: item.id,
      ...(note.trim() ? {
        note: note.trim()
      } : {})
    };
    switch (type) {
      case 'overboeken':
        return {
          ...base,
          type,
          from,
          to: target,
          qty: qty ?? 0
        };
      case 'verbruik':
        return {
          ...base,
          type,
          location,
          qty: qty ?? 0
        };
      case 'inkoop':
        return {
          ...base,
          type,
          location,
          qty: qty ?? 0,
          ...(cost !== undefined ? {
            costPriceCents: toCents(cost)
          } : {})
        };
      case 'afschrijven':
        return {
          ...base,
          type,
          location,
          qty: qty ?? 0,
          cause
        };
      case 'telling':
        return {
          ...base,
          type,
          location,
          counted: counted ?? -1,
          expected: item.levels[location]
        };
    }
  })();
  // Per inhoud. Niet vernieuwen na een fout: bij een weggevallen antwoord kan de boeking wél gelukt
  // zijn, en dan moet de herhaling dat antwoord krijgen in plaats van de voorraad twee keer te muteren.
  const requestId = useRequestId(JSON.stringify(body));
  const problem = movementProblem(body, item.levels);
  const level = item.levels[location];
  const diff = type === 'telling' && counted !== undefined ? counted - (level ?? 0) : null;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (problem) return;
    const req: StockMovementRequest = {
      requestId,
      ...body
    };
    const next = await submit(req);
    if (!next) return;
    toast.success(success[type], {
      description: item.name
    });
    onDone(next);
  };
  return <form onSubmit={e => void save(e)} noValidate className="space-y-3 rounded-md bg-white p-4" data-id="element-4985" data-mp="kcrt7a5pzyaj">
      <p className="text-[13px] text-muted" data-id="element-4986" data-mp="gczq3cz3ikck">{intro[type]}</p>

      <div className="flex flex-wrap items-end gap-3" data-id="element-4987" data-mp="2erkiysjx9uuk">
        <label className="block" data-id="element-4988" data-mp="2er111bu9v6gj">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-4989" data-mp="2ep844o9go0lq">{type === 'overboeken' ? 'Van' : 'Plek'}</span>
          <select value={location} onChange={e => setLocation(e.target.value as StockLocation)} className={`${inputCls} w-36`} data-id="element-4990" data-mp="3snlv90usjkdy">
            {stockLocations.map(l =>
          // Overboeken vanaf een plek die niet bijgehouden wordt kan niet (core/rules/stockMovements.ts).
          <option key={l} value={l} disabled={type === 'overboeken' && item.levels[l] === null} data-id="element-4991" data-mp="6adm78ewtl0p">
                {locationLabel[l]} ({item.levels[l] ?? '—'})
              </option>)}
          </select>
        </label>
        {type === 'overboeken' && <label className="block" data-id="element-4992" data-mp="3eocr4zjv0gqc">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4993" data-mp="3ipehsrzpnkgp">Naar</span>
            <select value={target} onChange={e => setTo(e.target.value as StockLocation)} className={`${inputCls} w-36`} data-id="element-4994" data-mp="3io3eeta6geq4">
              {stockLocations.filter(l => l !== from).map(l => <option key={l} value={l} data-id="element-4995" data-mp="12y0dkrx4md7a">
                    {locationLabel[l]}
                  </option>)}
            </select>
          </label>}
        {type === 'telling' ? <label className="block" data-id="element-4996" data-mp="2v03bm7d03bn9">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-4997" data-mp="2uz1r7ca3hcsv">Geteld</span>
            <NumberInput allowEmpty value={counted} onChange={setCounted} min={0} className="w-24" data-id="element-4998" data-mp="38zup86zluq0q" />
          </label> : <label className="block" data-id="element-4999" data-mp="3411884l40gvt">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-5000" data-mp="2gax6k2rher7g">Aantal</span>
            <NumberInput allowEmpty value={qty} onChange={setQty} min={1} className="w-24" data-id="element-5001" data-mp="2g8ui3n5z964u" />
          </label>}
        {type === 'inkoop' && <label className="block" data-id="element-5002" data-mp="2q1066lt3z3er">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-5003" data-mp="2q21lx3l2ke2t">Inkoopprijs per stuk (€, excl. btw)</span>
            <NumberInput decimals allowEmpty value={cost} onChange={setCost} className="w-32" data-id="element-5004" data-mp="2l7xb298onlwg" />
          </label>}
        {type === 'afschrijven' && <label className="block" data-id="element-5005" data-mp="1skbazaynyzec">
            <span className="mb-1 block text-[12px] text-muted" data-id="element-5006" data-mp="1sj8kh4596yyy">Waarom</span>
            <select value={cause} onChange={e => setCause(e.target.value as WriteOffCause)} className={`${inputCls} w-48`} data-id="element-5007" data-mp="81qzho4niq3s">
              {causes.map(c => <option key={c.key} value={c.key} data-id="element-5008" data-mp="3v8jkp2wp65z5">
                  {c.label}
                </option>)}
            </select>
          </label>}
      </div>

      {diff !== null && <p className="text-[13px] text-ink" aria-live="polite" data-id="element-5009" data-mp="qtjs3hft69az">
          {level === null ? `Vanaf nu bijgehouden: ${counted}.` : diff === 0 ? 'Klopt precies, er verandert niets.' : `Verschil ${diff > 0 ? '+' : ''}${diff}.`}
        </p>}

      <label htmlFor={`${id}-note`} className="block" data-id="element-5010" data-mp="hstx2fydgmyb">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-5011" data-mp="mrf0eo8x7mqz">Notitie (optioneel)</span>
        <input id={`${id}-note`} value={note} onChange={e => setNote(e.target.value)} maxLength={200} className={inputCls} data-id="element-5012" data-mp="32it66sr5l22i" />
      </label>

      {(touched && problem || error) && <p role="alert" className="text-[12px] text-clay" data-id="element-5013" data-mp="22v0dzevlhbs">
          {error ?? problem}
        </p>}

      <div className="flex flex-wrap gap-2" data-id="element-5014" data-mp="3u75oony9ci78">
        {/* Afschrijven is weg zonder verkoop: de knop zegt dat, in de waarschuwingskleur. */}
        <button type="submit" disabled={submitting} aria-busy={submitting} className={type === 'afschrijven' ? btnDestructive : btnPrimary} data-id="element-5015" data-mp="3p8cizhp7pvhm">
          {submitting && <Spinner data-id="element-5016" data-mp="lqu9l5tskgf3" />}
          {type === 'afschrijven' ? `${qty ?? ''} afschrijven`.trim() : type === 'telling' ? 'Telling opslaan' : 'Boeken'}
        </button>
        <button type="button" onClick={onCancel} disabled={submitting} className={btnGhost} data-id="element-5017" data-mp="gu3xsup0s6zh">
          Annuleren
        </button>
      </div>
    </form>;
}